import Cookies from 'js-cookie';

import useCookies from 'hooks/useCookies';
import { matrixConstants } from 'utils/Constants';
import { HAS_IMPORT_ERROR, RELOAD_TIMESTAMP } from 'utils/lazyWithRetry';

/**
 * Hook for managing Matrix authentication cookies and state
 *
 * Provides access to and control over Matrix-related cookies including:
 * - Matrix Access token for authentication
 * - Matrix Refresh token for silent session renewal
 * - Matrix Access token expiry (ISO timestamp)
 * - Matrix Search secret for encrypted room search key derivation
 * - Matrix Device ID for the current session
 * - Matrix User ID and display name
 * - Matrix Login status
 * - Matrix Auth issuer information
 * - Matrix Display name
 * - Matrix Stores version
 *
 * Also provides method to clear all cookies.
 */

//TODO: remove later, when transition is over
const removeLegacyCookieDomain = (key: string) => {
  const host = window.location.hostname;

  Cookies.remove(key, { domain: `.${host}` });
};

const useMxCookies = () => {
  const [mxAccessToken, setMxAccessToken] = useCookies<string | undefined>(matrixConstants.MX_ACCESS_TOKEN, undefined);
  const [mxDeviceId, setMxDeviceId] = useCookies<string | undefined>(matrixConstants.MX_DEVICE_ID, undefined);
  const [mxUserId, setMxUserId] = useCookies<string | undefined>(matrixConstants.MX_USER_ID, undefined);
  const [mxDisplayName, setMxDisplayName] = useCookies<string | undefined>(matrixConstants.MX_DISPLAY_NAME, undefined);
  const [mxLoggedIn, setMxLoggedIn] = useCookies<boolean | undefined>(matrixConstants.MX_LOGGED_IN, undefined);
  const [mxRefreshToken, setMxRefreshToken] = useCookies<string | undefined>(
    matrixConstants.MX_REFRESH_TOKEN,
    undefined
  );
  const [mxAccessTokenExpiresAt, setMxAccessTokenExpiresAt] = useCookies<string | undefined>(
    matrixConstants.MX_ACCESS_TOKEN_EXPIRES_AT,
    undefined
  );
  const [mxSearchSecret, setMxSearchSecret] = useCookies<string | undefined>(
    matrixConstants.MX_SEARCH_SECRET,
    undefined
  );
  const [matrixStoresVersion, setMatrixStoresVersion] = useCookies<boolean | undefined>(
    matrixConstants.MATRIX_STORES_VERSION,
    undefined
  );

  const clearCookies = () => {
    setMxAccessToken(undefined);
    setMxRefreshToken(undefined);
    setMxAccessTokenExpiresAt(undefined);
    setMxSearchSecret(undefined);
    setMxDeviceId(undefined);
    setMxUserId(undefined);
    setMxDisplayName(undefined);
    setMxLoggedIn(undefined);

    // Remove legacy domain variants
    [
      matrixConstants.MX_ACCESS_TOKEN,
      matrixConstants.MX_REFRESH_TOKEN,
      matrixConstants.MX_ACCESS_TOKEN_EXPIRES_AT,
      matrixConstants.MX_SEARCH_SECRET,
      matrixConstants.MX_DEVICE_ID,
      matrixConstants.MX_USER_ID,
      matrixConstants.MX_DISPLAY_NAME,
      matrixConstants.MX_LOGGED_IN,
      matrixConstants.MATRIX_STORES_VERSION,
      HAS_IMPORT_ERROR,
      RELOAD_TIMESTAMP,
    ].forEach(removeLegacyCookieDomain);
  };

  return {
    mxAccessToken,
    mxRefreshToken,
    mxAccessTokenExpiresAt,
    mxSearchSecret,
    mxDeviceId,
    mxUserId,
    mxDisplayName,
    mxLoggedIn,
    matrixStoresVersion,
    setMxAccessToken,
    setMxRefreshToken,
    setMxAccessTokenExpiresAt,
    setMxSearchSecret,
    setMxDeviceId,
    setMxUserId,
    setMxDisplayName,
    setMxLoggedIn,
    setMatrixStoresVersion,
    clearCookies,
  };
};

export default useMxCookies;
